import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Receipt, Loader2, Lock, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) return setError("Password must be at least 8 characters");
    if (pwd !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Receipt className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="font-heading font-semibold text-slate-900 text-lg">Tax Flow</div>
        </div>

        {!token ? (
          <div className="text-center text-slate-500 text-sm" data-testid="reset-no-token">
            Invalid reset link. <Link to="/forgot-password" className="text-blue-600 hover:underline">Request a new one</Link>.
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-xl font-semibold text-slate-900">Password reset</h1>
            <p className="text-slate-500 text-sm mt-2">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-xl font-semibold text-slate-900">Set a new password</h1>
            <p className="text-slate-500 text-sm mt-1.5 mb-6">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)}
                  placeholder="New password" data-testid="reset-password"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Confirm new password" data-testid="reset-confirm"
                  className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              {error && <div className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Reset password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
